import {
  type MouseEventHandler,
  type ReactNode,
  forwardRef,
  useId,
  useMemo,
} from 'react';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type AriaDisabledProps,
  type AriaLabelingProps,
  type DOMProps,
  type DataTestId,
  type StylingProps,
  _useAriaLabelingProps,
  _useSafeForwardProps,
} from '@dynatrace/strato-components/core';

import { useBaseInputContext } from './hooks/useBaseInputContext.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';

//#region Typings
/**
 * Accepted properties for the BaseInput Button
 * @public
 */
export interface BaseInputButtonProps
  extends AriaLabelingProps,
    AriaDisabledProps,
    DOMProps,
    StylingProps,
    DataTestId {
  /** Elements to be displayed in the BaseInput Button slot, preferably an icon or a similar visual element */
  children: ReactNode;
  /**
   * If a button is disabled e.g. it cannot be interacted with.
   * By default, it inherits the disabled prop of the BaseInput, but can be overwritten.
   * @defaultValue false
   */
  disabled?: boolean;
  /**
   * If specified, a tooltip is rendered with this text, which describes what the button does.
   * Please also consider translating the text in an internationalized context and use a short text description for the tooltip.
   * The positioning and placing of the according tooltip will be handled by the component.
   */
  tooltipText?: string;
  /** Called when the button is interacted with. */
  onClick?: MouseEventHandler;
  /** Determines whether the input is in read-only mode. */
  readOnly?: boolean;
}

//#endregion

/**
 * Component which allows users to trigger actions or events when clicking or when pressing `Enter` or `Space` while the button has focus.
 * @public
 */
export const BaseInputButton = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  BaseInputButtonProps
>((props, forwardedRef) => {
  const {
    children,
    tooltipText,
    disabled: propDisabled,
    id,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'aria-disabled': ariaDisabledProp,
    readOnly,
    ...remainingProps
  }: BaseInputButtonProps = props;

  const {
    hasError = false,
    disabled,
    variant,
    ariaDisabled: contextAriaDisabled,
  } = useBaseInputContext();
  const ariaDisabled = ariaDisabledProp || contextAriaDisabled;

  const tooltipGeneratedId = useId();
  const tooltipId = tooltipText ? id ?? tooltipGeneratedId : undefined;

  const { 'aria-labelledby': ariaLabelledByProp, ...remainingAriaProps } =
    _useAriaLabelingProps(remainingProps);

  const forwardProps = _useSafeForwardProps(remainingProps, {
    ariaLabelledByProp,
    ...remainingAriaProps,
  });

  const ariaLabelledBy = useMemo(
    () =>
      tooltipId || ariaLabelledByProp
        ? `${tooltipId ? tooltipId + ' ' : ''}${ariaLabelledByProp ?? ''}`
        : undefined,
    [ariaLabelledByProp, tooltipId],
  );

  const button = useMemo(
    () => (
      <Button
        ref={forwardedRef}
        {...forwardProps}
        variant={variant === 'minimal' ? 'default' : 'emphasized'}
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        readOnly={readOnly}
        disabled={propDisabled ?? disabled}
        aria-disabled={ariaDisabled}
        color={hasError && !disabled ? 'critical' : 'neutral'}
        aria-labelledby={ariaLabelledBy}
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        {...remainingAriaProps}
      >
        <Button.Prefix>{children}</Button.Prefix>
      </Button>
    ),
    [
      ariaLabelledBy,
      children,
      consumerClassName,
      consumerStyle,
      dataTestId,
      disabled,
      forwardProps,
      forwardedRef,
      hasError,
      propDisabled,
      remainingAriaProps,
      variant,
      readOnly,
      ariaDisabled,
    ],
  );

  return tooltipText ? (
    <Tooltip id={tooltipId} text={tooltipText} placement="top-end">
      {button}
    </Tooltip>
  ) : (
    button
  );
});

(
  BaseInputButton as typeof BaseInputButton & { displayName: string }
).displayName = 'BaseInput.Button';
